"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { BannerItem } from "@/api/useGetBannerApi";

const STORAGE_PREFIX = "banner_hidden_";

const isHiddenForDay = (id: number): boolean => {
  if (typeof window === "undefined") return false;
  const value = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
  return !!value && Date.now() < Number(value);
};

const hideForDay = (id: number) => {
  localStorage.setItem(`${STORAGE_PREFIX}${id}`, String(Date.now() + 24 * 60 * 60 * 1000));
};

interface BannerCardProps {
  banner: BannerItem;
  className?: string;
  onClose: (id: number) => void;
  onHideForDay: (id: number) => void;
}

const BannerCard = ({ banner, className, onClose, onHideForDay }: BannerCardProps) => {
  const { t } = useTranslation();
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const hasValidImageUrl = typeof banner.imageUrl === "string" && banner.imageUrl.trim().length > 0;

  const image = !hasValidImageUrl || imageLoadFailed ? (
    <div className="w-full min-h-[220px] flex flex-col items-center justify-center gap-3 bg-gray-100 text-gray-500">
      <svg
        className="w-10 h-10 text-gray-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
      <p className="text-sm font-medium">
        {t("BANNER_IMAGE_LOAD_FAILED", { defaultValue: "이미지를 불러올 수 없습니다." })}
      </p>
    </div>
  ) : (
    <Image
      src={banner.imageUrl}
      alt={banner.title}
      width={360}
      height={0}
      sizes="360px"
      style={{ width: "100%", height: "auto" }}
      loading="lazy"
      unoptimized
      onError={() => setImageLoadFailed(true)}
    />
  );

  return (
    <div className={`flex flex-col bg-white border border-gray-200 ${className || ""}`}>
      {banner.linkUrl ? (
        <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="block w-full">
          {image}
        </a>
      ) : (
        <div className="w-full">{image}</div>
      )}
      <div className="flex w-full border-t border-gray-200">
        <button
          onClick={() => onHideForDay(banner.id)}
          className="flex-1 py-3 text-sm text-gray-500 hover:bg-gray-100 transition-colors"
        >
          {t("BANNER_HIDE_FOR_DAY")}
        </button>
        <button
          onClick={() => onClose(banner.id)}
          className="flex-1 py-3 text-sm text-gray-700 font-medium hover:bg-gray-100 transition-colors border-l border-gray-200"
        >
          {t("BANNER_CLOSE")}
        </button>
      </div>
    </div>
  );
};

interface BannerPopupProps {
  bannerList: BannerItem[];
}

const BannerPopup = ({ bannerList }: BannerPopupProps) => {
  const [dismissedIds, setDismissedIds] = useState<number[]>([]);

  const handleClose = (id: number) => {
    setDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleHideForDay = (id: number) => {
    hideForDay(id);
    setDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const visibleBanners = bannerList.filter(
    (b) => !isHiddenForDay(b.id) && !dismissedIds.includes(b.id),
  );

  if (visibleBanners.length === 0) return null;

  const cardProps = { onClose: handleClose, onHideForDay: handleHideForDay };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60" />

      <div className="fixed inset-0 z-50 flex items-center justify-center md:hidden">
        <BannerCard
          banner={visibleBanners[0]}
          className="w-[calc(100vw-2rem)] max-w-[360px]"
          {...cardProps}
        />
      </div>

      <div className="hidden fixed top-25 left-25 z-50 md:flex items-start">
        {visibleBanners.map((banner) => (
          <div key={banner.id}>
            <BannerCard banner={banner} className="w-[360px]" {...cardProps} />
          </div>
        ))}
      </div>
    </>
  );
};

export default BannerPopup;
